/**
 * What a dock body says instead of drawing an empty plot (spec §B5). The PASS
 * clearance run is the first user: a chart of sixty zero-height bars on a
 * 0–1 axis read as "the data did not load" when it meant "nothing came near"
 * (audit C7). The body says it in words, with a way on.
 *
 * Muted, not a coloured banner: the verdict chip in the header already
 * carries the colour; this is the explanation under it. The `pass` tone only
 * tints the icon.
 */
import type { ReactNode } from 'react';

import { CircleCheck, Info } from 'lucide-react';

import { cn } from '@/lib/utils';

import { DOCK_CONTROL } from './dockStyles';

export interface DockEmptyStateProps {
  /** One sentence: what is (not) there. */
  title: ReactNode;
  /** Optional second line: why, or what it was measured against. */
  body?: ReactNode;
  /** `DockEmptyStateAction`s — "Show near-miss chart", "Open table". */
  actions?: ReactNode;
  /** `pass` draws a green check; `neutral` (default) an ⓘ. */
  tone?: 'pass' | 'neutral';
  /** Replaces the tone's icon. */
  icon?: ReactNode;
  testId?: string;
  className?: string;
}

export const DockEmptyState = ({ title, body, actions, tone = 'neutral', icon, testId, className }: DockEmptyStateProps) => (
  <div data-testid={testId} className={cn('flex min-h-0 flex-1 items-center justify-center p-4', className)}>
    <div className='flex max-w-md items-start gap-3'>
      <span
        aria-hidden
        className={cn(
          'mt-0.5 shrink-0',
          tone === 'pass' ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-stone-500',
        )}
      >
        {icon ?? (tone === 'pass' ? <CircleCheck className='size-5' /> : <Info className='size-5' />)}
      </span>
      <div className='flex min-w-0 flex-col gap-1'>
        <p className='text-[13px] leading-5 font-medium text-slate-900 dark:text-white'>{title}</p>
        {body != null && <p className='text-slate-500 dark:text-stone-400'>{body}</p>}
        {actions != null && <div className='mt-1 flex flex-wrap items-center gap-x-4 gap-y-1'>{actions}</div>}
      </div>
    </div>
  </div>
);

export interface DockEmptyStateActionProps {
  children: ReactNode;
  onClick: () => void;
  testId?: string;
}

/** A text-link action for an empty state — an action, so a `<button>`. */
export const DockEmptyStateAction = ({ children, onClick, testId }: DockEmptyStateActionProps) => (
  <button
    type='button'
    data-testid={testId}
    onClick={onClick}
    className={cn(
      'rounded-sm font-medium text-sky-700 underline-offset-2 hover:underline dark:text-sky-400',
      DOCK_CONTROL.focus,
    )}
  >
    {children}
  </button>
);
